import { RETURN_METHOD, ReturnMethodType } from '../constants/general';
import { IReturnEligibilityResponse } from '../models/api';
import {
  ItemEligibilities,
  ItemToReturn,
  OrderDetailsLineItemSessionObj,
  ReturnFormItem,
} from '../models/returns';
import { getFeatureSwitchStatic } from './general';

//* Return Allowed comes from OMS. */
export type ReturnAllowedFields =
  | 'returnOnlineAllowed'
  | 'returnInStoreAllowed'
  | 'returnLORPAllowed'
  | 'returnDropoffAllowed';
export const ReturnAllowedMap = {
  [RETURN_METHOD.UPS_DROP_OFF]: 'returnDropoffAllowed' as ReturnAllowedFields,
  // TODO remove this mapping when SORIS is live
  [RETURN_METHOD.IN_STORE]: 'returnInStoreAllowed' as ReturnAllowedFields,
  [RETURN_METHOD.LORP]: 'returnLORPAllowed' as ReturnAllowedFields,
  [RETURN_METHOD.SORIS]: 'returnInStoreAllowed' as ReturnAllowedFields,
  [RETURN_METHOD.APO_FPO]: 'returnDropoffAllowed' as ReturnAllowedFields,
};

//* Return Eligibility comes from CheckEligibilityAPI. */
export type EligibilityItemFields =
  | 'returnEligible'
  | 'sorisReturnEligible'
  | 'lorpReturnEligible';
export const CheckEligibilityMap = {
  [RETURN_METHOD.UPS_DROP_OFF]: 'returnEligible' as EligibilityItemFields,
  [RETURN_METHOD.IN_STORE]: 'sorisReturnEligible' as EligibilityItemFields,
  [RETURN_METHOD.LORP]: 'lorpReturnEligible' as EligibilityItemFields,
  [RETURN_METHOD.SORIS]: 'sorisReturnEligible' as EligibilityItemFields,
  [RETURN_METHOD.APO_FPO]: 'returnEligible' as EligibilityItemFields,
};

export interface ReturnEligibilityDict {
  [lineNumber: string]: boolean;
}

// DEPRECATED: Converting array to dictionary for easier traversal
export const convertReturnEligibilityItemsToDict = (
  lineItems: IReturnEligibilityResponse['lineItems'] = [],
  eligibilityType: EligibilityItemFields,
) =>
  lineItems.reduce(
    (obj: ReturnEligibilityDict, item) => ({
      ...obj,
      [item.lineNumber]: item[eligibilityType] || false,
    }),
    {},
  );

/**
 * Checks if lineItems are eligible for returns based on OMS eligibility and CheckEligibilityAPI response.
 * @param item Line item from order details
 * @param itemCheckEligibility Boolean indicating if the item is eligible based on CheckEligibilityAPI
 * @param eligibilityField Field to check for OMS return eligibility
 */
export const isItemEligible = (
  item: OrderDetailsLineItemSessionObj,
  itemCheckEligibility: boolean,
  eligibilityField: ReturnAllowedFields,
): boolean => {
  let omsReturnEligible = item[eligibilityField];
  if (eligibilityField === 'returnDropoffAllowed') {
    omsReturnEligible = Boolean(
      item.returnDropoffAllowed || item.returnOnlineAllowed,
    );
  }

  if (omsReturnEligible === undefined) {
    return Boolean(itemCheckEligibility);
  }
  return omsReturnEligible && itemCheckEligibility;
};

/**
 * Mapper function to maintain existing flow in <ItemsToReturn>
 * @param lineItems lineItems from selected fulfillment group
 * @param returnEligibilityDetails API response frmo return/eligibility
 * @returns SingleItemFromReturnsApi object
 */
export const mapToSingleItemFromReturnsApiList = (
  lineItems: OrderDetailsLineItemSessionObj[],
  returnEligibilityDetails: IReturnEligibilityResponse | undefined,
  returnMethod: ReturnMethodType,
): ReturnFormItem[] => {
  const checkEligibilityField = CheckEligibilityMap[returnMethod];
  const returnAllowedField = ReturnAllowedMap[returnMethod];
  const returnEligibilityLineItemMap = (
    returnEligibilityDetails?.lineItems || []
  ).reduce<Record<string, IReturnEligibilityResponse['lineItems'][number]>>(
    (itemMap, lineItem) => {
      return {
        ...itemMap,
        [lineItem.lineNumber]: lineItem,
      };
    },
    {},
  );

  return lineItems.map((item: OrderDetailsLineItemSessionObj) => {
    const itemCheckElig = getItemCheckEligibility(
      returnEligibilityDetails?.lineItems,
      item.lineId,
      checkEligibilityField,
    );
    const itemEligibility = isItemEligible(
      item,
      itemCheckElig,
      returnAllowedField,
    );
    return {
      brandName: item.brandName,
      department: item.departmentNumber,
      isDFCSourced: item.isDFCSourced,
      classNumber: item.classId,
      itemCost: item.unitPrice || 0,
      shippingCharge: item.shippingCharge || 0,
      unitCost: item.unitCost,
      itemDescription: item.description,
      itemImageURL: item.imageUrl,
      itemSubClass: item.subClassId,
      lineItemId: item.lineId,
      mVendorNumber: item.vendorNumber,
      vendorPartNumber: item.vendorPartNumber,
      poNumber: item.PONumber,
      quantityOrdered: item.orginalOrderedQuantity,
      currentQuantity: item.currentQuantity,
      upcCode: item.upcCode,
      vendorName: item.vendorName,
      sku: item.skuNumber,
      omsId: item.omsId,
      secOmsId: item.secOmsId,
      returnEligibilityInfo: {
        returnEligible: itemEligibility,
      },
      hazmatItem: returnEligibilityLineItemMap[item.lineId]?.hazmatItem,
      chargeIdentifier: item.chargeIdentifier,
      totalPrice: item.totalPrice,
      fulfillmentVendorNumber: item.fulfillmentVendorNumber,
      itemWeight: item.itemWeight,
      productType: item.productType,
      returnWindowEndDate: item.returnWindowEndDate,
      relationType: item.relationType,
      isStatusDeliveredOrOrderPickedup:
        item.isStatusDeliveredOrOrderPickedup === undefined
          ? true
          : item.isStatusDeliveredOrOrderPickedup,
    };
  });
};

/**
 * Mapper function to transform the session item to the smaller SelectItem that we save to the state <ItemsToReturn>
 * @param lineItems lineItems from selected fulfillment group
 * @param returnEligibilityDetails API response frmo return/eligibility
 * @returns SingleItemFromReturnsApi object
 */
export const mapToReturnFormItem = (
  lineItems: OrderDetailsLineItemSessionObj[],
  returnEligibilityDetails: IReturnEligibilityResponse | undefined,
): ReturnFormItem[] => {
  const returnEligibilityLineItemMap = (
    returnEligibilityDetails?.lineItems || []
  ).reduce<Record<string, IReturnEligibilityResponse['lineItems'][number]>>(
    (itemMap, lineItem) => {
      return {
        ...itemMap,
        [lineItem.lineNumber]: lineItem,
      };
    },
    {},
  );

  return lineItems.map(
    (item: OrderDetailsLineItemSessionObj): ReturnFormItem => {
      return {
        brandName: item.brandName,
        department: item.departmentNumber,
        isDFCSourced: item.isDFCSourced,
        classNumber: item.classId,
        itemCost: item.unitPrice || 0,
        shippingCharge: item.shippingCharge || 0,
        unitCost: item.unitCost,
        itemDescription: item.description,
        itemImageURL: item.imageUrl,
        itemSubClass: item.subClassId,
        lineItemId: item.lineId,
        mVendorNumber: item.vendorNumber,
        vendorPartNumber: item.vendorPartNumber,
        poNumber: item.PONumber,
        quantityOrdered: item.orginalOrderedQuantity,
        currentQuantity: item.currentQuantity,
        upcCode: item.upcCode,
        vendorName: item.vendorName,
        sku: item.skuNumber,
        omsId: item.omsId,
        secOmsId: item.secOmsId,
        eligibilities: getItemEligibilities(item, returnEligibilityDetails),
        hazmatItem: returnEligibilityLineItemMap[item.lineId]?.hazmatItem,
        chargeIdentifier: item.chargeIdentifier,
        totalPrice: item.totalPrice,
        fulfillmentVendorNumber: item.fulfillmentVendorNumber,
        itemWeight: item.itemWeight,
        productType: item.productType,
        returnWindowEndDate: item.returnWindowEndDate,
        relationType: item.relationType,
        isStatusDeliveredOrOrderPickedup:
          item.isStatusDeliveredOrOrderPickedup === undefined
            ? true
            : item.isStatusDeliveredOrOrderPickedup,
      };
    },
  );
};

/**
 * Mapper function to transform the session item to the smaller SelectItem that we save to the state <ItemsToReturn>
 * @param lineItems lineItems from selected fulfillment group
 * @param returnEligibilityDetails API response frmo return/eligibility
 * @returns SingleItemFromReturnsApi object
 */
export const mapToItemToReturn = (
  lineItems: OrderDetailsLineItemSessionObj[],
  returnEligibilityDetails: IReturnEligibilityResponse | undefined,
  isApoFpoEligible = false,
): ItemToReturn[] => {
  const returnEligibilityLineItemMap = (
    returnEligibilityDetails?.lineItems || []
  ).reduce<Record<string, IReturnEligibilityResponse['lineItems'][number]>>(
    (itemMap, lineItem) => {
      return {
        ...itemMap,
        [lineItem.lineNumber]: lineItem,
      };
    },
    {},
  );

  return lineItems.map((item: OrderDetailsLineItemSessionObj): ItemToReturn => {
    return {
      brandName: item.brandName,
      classNumber: item.classId,
      shippingCharge: item.shippingCharge || 0,
      depNbr: item.departmentNumber,
      mVendorNumber: item.vendorNumber,
      imageUrl: item.imageUrl,
      itemCost: item.unitPrice,
      unitCost: item.unitCost,
      itemSubClass: item.subClassId,
      itemDescription: item.description,
      lineItemId: item.lineId,
      omsId: item.omsId || item.secOmsId,
      upcCode: item.upcCode,
      poNumber: item.PONumber,
      isDFCSourced: item.isDFCSourced,
      vendorName: item.vendorName,
      vendorPartNumber: item.vendorPartNumber,
      itemImageURL: item.imageUrl,
      sku: item.skuNumber,
      chargeIdentifier: item.chargeIdentifier,
      totalPrice: item.totalPrice,
      fulfillmentVendorNumber: item.fulfillmentVendorNumber,
      itemWeight: item.itemWeight,
      productType: item.productType,
      returnWindowEndDate: item.returnWindowEndDate,
      hazmatItem: returnEligibilityLineItemMap[item.lineId]?.hazmatItem,
      // these are new now
      relationType: item.relationType,
      quantityOrdered: item.orginalOrderedQuantity,
      currentQuantity: item.currentQuantity,
      eligibilities: getItemEligibilities(
        item,
        returnEligibilityDetails,
        isApoFpoEligible,
      ),
      isStatusDeliveredOrOrderPickedup:
        item.isStatusDeliveredOrOrderPickedup === undefined
          ? true
          : item.isStatusDeliveredOrOrderPickedup,
      // These get changed in the component
      returnDispCode: '',
      returnPolicyId: 0,
      returnQuantity: 1,
      returnReasonCode: '',
      rtvTokenId: '',
      returnReasonText: '',
    };
  });
};

const getItemCheckEligibility = (
  lineItems: IReturnEligibilityResponse['lineItems'] = [],
  lineId: string,
  eligibilityField: EligibilityItemFields,
): boolean => {
  const item = lineItems.find((lineItem) => lineItem.lineNumber === lineId);
  if (!item) return false;
  return Boolean(item[eligibilityField]);
};

export const getItemEligibilities = (
  lineItem: OrderDetailsLineItemSessionObj | undefined,
  returnEligibilityDetails: IReturnEligibilityResponse | undefined,
  isApoFpoEligible = false,
): ItemEligibilities => {
  if (!returnEligibilityDetails || !lineItem)
    return {
      anyEligibility: false,
      [RETURN_METHOD.LORP]: false,
      [RETURN_METHOD.UPS_DROP_OFF]: false,
      [RETURN_METHOD.IN_STORE]: false,
      [RETURN_METHOD.SORIS]: false,
      [RETURN_METHOD.APO_FPO]: false,
    };

  const item = returnEligibilityDetails.lineItems.find(
    (line) => line.lineNumber === lineItem.lineId,
  );
  const upsEligible =
    item?.returnEligible &&
    (lineItem.returnDropoffAllowed || lineItem.returnOnlineAllowed);
  const allowSORIS = getFeatureSwitchStatic('allowSORIS');
  const instoreEligible = lineItem.returnInStoreAllowed;
  const sorisEligible = allowSORIS
    ? lineItem.returnInStoreAllowed && item?.sorisReturnEligible
    : false;
  const lorpEligible = item?.lorpReturnEligible && lineItem.returnLORPAllowed;

  return {
    anyEligibility: Boolean(
      upsEligible || lorpEligible || instoreEligible || sorisEligible,
    ),
    [RETURN_METHOD.LORP]: Boolean(lorpEligible),
    [RETURN_METHOD.UPS_DROP_OFF]: Boolean(upsEligible),
    [RETURN_METHOD.IN_STORE]: Boolean(instoreEligible),
    [RETURN_METHOD.SORIS]: Boolean(sorisEligible),
    [RETURN_METHOD.APO_FPO]: Boolean(upsEligible && isApoFpoEligible),
  };
};
